import { useEffect, useState } from "react";
import { api_private_get, formatStringAmountCredits, showAlert } from "script";
import "../../../components/LeaderboardTab.css";

function winRate(entry) {
  return entry.predictions > 0
    ? `${Math.round((entry.wins / entry.predictions) * 100)}%`
    : "-";
}

function Rank({ value }) {
  if (value > 3) return <span className="leaderboard-rank">{value}</span>;
  return (
    <span className={`leaderboard-rank leaderboard-rank-${value}`}>
      <i className="ti ti-trophy" aria-hidden="true"></i>
      <span className="visually-hidden">Rank </span>
      {value}
    </span>
  );
}

function pnlClass(value) {
  if (value > 0) return "leaderboard-positive";
  if (value < 0) return "leaderboard-negative";
  return "";
}

function PnlCell({ total, realized, unrealized }) {
  return (
    <td className="leaderboard-pnl">
      <strong className={pnlClass(total)}>
        {formatStringAmountCredits(total, "credits")}
      </strong>
      <span>
        Realized{" "}
        <b className={pnlClass(realized)}>
          {formatStringAmountCredits(realized, "credits")}
        </b>
      </span>
      <span>
        Unrealized{" "}
        <b className={pnlClass(unrealized)}>
          {formatStringAmountCredits(unrealized, "credits")}
        </b>
      </span>
    </td>
  );
}

function LeaderboardRow({ entry }) {
  return (
    <tr className={entry.is_current_user ? "leaderboard-current-row" : ""}>
      <td>
        <Rank value={entry.rank} />
      </td>
      <td>
        <div className="leaderboard-player">
          <span>{entry.display_name}</span>
          {entry.is_current_user && (
            <span className="leaderboard-you">You</span>
          )}
        </div>
      </td>
      <td className="leaderboard-net-worth">
        {formatStringAmountCredits(entry.net_worth, "credits")}
      </td>
      <td>
        {formatStringAmountCredits(entry.holdings_value, "credits")}
      </td>
      <PnlCell
        total={entry.total_pnl_lifetime}
        realized={entry.realized_pnl_lifetime}
        unrealized={entry.unrealized_pnl_lifetime}
      />
      <PnlCell
        total={entry.total_pnl_30d}
        realized={entry.realized_pnl_30d}
        unrealized={entry.unrealized_pnl_30d}
      />
      <td>{entry.predictions.toLocaleString()}</td>
      <td>{entry.wins.toLocaleString()}</td>
      <td>{winRate(entry)}</td>
    </tr>
  );
}

export default function LeaderboardTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api_private_get("/account/leaderboard", (response) => {
      setLoading(false);
      if (!response?.success) {
        showAlert(response?.error || "Unable to load leaderboard.", "danger");
        return;
      }
      setData(response);
    });
  }, []);

  const currentUserIsVisible = data?.leaders.some(
    (entry) => entry.is_current_user,
  );

  return (
    <section className="leaderboard-panel p2-bg">
      <header className="leaderboard-heading">
        <div>
          <p className="leaderboard-eyebrow">Credits league</p>
          <h4>Leaderboard</h4>
          <p>Ranked by liquid balance plus current holdings value.</p>
        </div>
        <div className="leaderboard-heading-icon" aria-hidden="true">
          <i className="ti ti-crown"></i>
        </div>
      </header>

      {loading && (
        <div className="leaderboard-state" role="status">
          Loading rankings...
        </div>
      )}

      {!loading && data?.leaders.length === 0 && (
        <div className="leaderboard-state">No credits players yet.</div>
      )}

      {!loading && data?.leaders.length > 0 && (
        <>
          <div className="leaderboard-table-wrap">
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th scope="col">Rank</th>
                  <th scope="col">Player</th>
                  <th scope="col">Net worth</th>
                  <th scope="col">Holdings</th>
                  <th scope="col">Lifetime prediction P&amp;L</th>
                  <th scope="col">30-day prediction P&amp;L</th>
                  <th scope="col">Predictions</th>
                  <th scope="col">Wins</th>
                  <th scope="col">Win rate</th>
                </tr>
              </thead>
              <tbody>
                {data.leaders.map((entry, index) => (
                  <LeaderboardRow
                    key={`${entry.rank}-${entry.display_name}-${index}`}
                    entry={entry}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {!currentUserIsVisible && data.current_user && (
            <div className="leaderboard-own-rank">
              <span>Your position</span>
              <strong>#{data.current_user.rank}</strong>
              <span>
                {formatStringAmountCredits(
                  data.current_user.net_worth,
                  "credits",
                )}
              </span>
            </div>
          )}
        </>
      )}
    </section>
  );
}
